import { valueDataToString } from "@smartsuite/automation-conditions";
import type {
  ActionConfig,
  ActionOutputData,
  AutomationError,
  InputData,
  Job,
  OutputData,
  RecordJobData,
  RecordOutputData,
  Value,
} from "@smartsuite/automation-engine-api";
import {
  ErrorCode,
  JobStatus_Stage,
  Timestamp,
} from "@smartsuite/automation-engine-api";
import type { AppField } from "@smartsuite/smartsuite-client";
import type { ActivityRecord } from "../../../stores/jobs.store";
import { resolveFieldBySlug } from "@smartsuite/fields-logic/lib/utils/fields";
import { t } from "i18next";
import { translateIfPresent } from "../../../locales/i18n";
import type { LimitedJob } from "../../../services/engineClient";
import { collectActionsDeep } from "../../../stores/automation/utils/action-group-walk";
import type {
  ActionCandidate,
  ActionGroupCandidate,
} from "../../../types/candidate";
import { RECORD_ID } from "../../../utils/constants";
import { pickValueDataString } from "../../../utils/inputs";
import type { JobDetails } from "../ActivityDetails/components/ActivityDetails.types";
import type { ActivityGroupStatus } from "../ActivityDetails/components/ActivityDetailsGroup/ActivityDetailsGroup";

const TWO_HOURS = 2 * 60 * 60;
const OUTPUT_TEXT_LIMIT = 5000;

const getAppFieldBySlug = <TField extends AppField = AppField>(
  fields: AppField[],
  slug: string,
): TField | undefined => {
  // field_type string-union drift between @smartsuite/smartsuite-client (AppField)
  // and @smartsuite/types (AppStructureField, used by resolveFieldBySlug's
  // signature); same runtime shape — resolveFieldBySlug only reads slug/nested
  const [parentField, childField] = resolveFieldBySlug(
    fields as Parameters<typeof resolveFieldBySlug>[0],
    slug,
  );

  return (
    childField && parentField
      ? {
          ...childField,
          label: `${parentField.label} : ${childField.label}`,
          parent: parentField,
        }
      : parentField
  ) as TField;
};

export const filterFieldsBySlugList = (
  structure: AppField[],
  includeSlugs: string[] = [],
): AppField[] => {
  if (!structure || !includeSlugs) {
    return [];
  }

  const fields = includeSlugs
    .map((slug) => getAppFieldBySlug(structure, slug))
    .filter((field): field is AppField => !!field);

  return fields;
};

export const convertStatus = (job: LimitedJob): ActivityGroupStatus => {
  const stage = job.status?.stage;
  if (stage === undefined) {
    return "is_error";
  }
  switch (stage) {
    case JobStatus_Stage.COMPLETED:
      return "is_success";
    case JobStatus_Stage.ACTION_FAIL:
    case JobStatus_Stage.CYCLE_DETECTED:
    case JobStatus_Stage.AUTOMATION_DISABLED:
    case JobStatus_Stage.TRIGGER_FAIL:
      return "is_error";
    case JobStatus_Stage.CONDITIONS_NOT_MET:
      return "conditions_not_met";
    case JobStatus_Stage.TRIGGER_REJECTED:
      return "rejected";
    case JobStatus_Stage.STARTED:
    case JobStatus_Stage.WAITING:
      if (
        job.triggerTime !== undefined &&
        Number(Timestamp.now().seconds) -
          Number(job.retryStatus?.retryAt?.seconds ?? job.triggerTime.seconds) >
          TWO_HOURS
      ) {
        return "is_error";
      }
      return "is_pending";
  }
};

export const getErrorMessage = (action: ActionOutputData, job: Job): string => {
  switch (action.status.oneofKind) {
    case "ok":
      return "";
    case "recordListProcessing":
      return getJobErrorMessage(job) ?? t("job-list.action-failed");
    case "error":
      return getErrorText(action.status.error.error);
    case "fail":
      return action.status.fail.message;
    case "fieldMissing":
      return action.status.fieldMissing.message;
    case "invalidCredential":
      return action.status.invalidCredential.message;
    case "inputMissing":
      return action.status.inputMissing.message;
    default:
      return "";
  }
};

export const getErrorText = (error: AutomationError | undefined): string => {
  if (error === undefined) {
    return "";
  }
  switch (error.code) {
    case ErrorCode.UNKNOWN_ERROR:
      if (error.message !== "") {
        return `${t("errors.server-error")}: ${error.message}`;
      }
      return t("errors.server-error");
    case ErrorCode.NO_EMAILS:
      return t("errors.no-emails");
    case ErrorCode.OUTPUT_MISSING:
      return t("errors.output-missing", {
        fieldId: getValueFromStructField(error.attributes?.fields.fieldId),
      });
    case ErrorCode.OUTPUT_MISSING_FOR_ACTION_INPUT:
      return t("errors.output-missing-for-action-input", {
        fieldId: getValueFromStructField(error.attributes?.fields.fieldId),
      });
    case ErrorCode.OUTPUT_MISSING_FOR_ACTION_CONDITION:
      return t("errors.output-missing-for-action-condition", {
        fieldId: getValueFromStructField(error.attributes?.fields.fieldId),
      });
    case ErrorCode.INPUT_MISSING:
      return t("errors.input-missing", {
        missingInputs: getValueFromStructField(
          error.attributes?.fields.missingInputs,
        ),
      });
    case ErrorCode.MATH_OPERATION_EMPTY_VALUE:
      return t("errors.input-missing", {
        missingInputs: getValueFromStructField(
          error.attributes?.fields.missingInputs,
        ),
      });
    case ErrorCode.GENERAL_ERROR:
      if (
        error.attributes?.fields.message !== undefined &&
        error.attributes?.fields.message.kind.oneofKind === "stringValue"
      ) {
        return error.attributes?.fields.message.kind.stringValue;
      } else if (error.message !== "") {
        return `${t("errors.server-error")}: ${error.message}`;
      }
      return t("errors.server-error");
    case ErrorCode.INVALID_CREDENTIALS:
      return t("errors.invalid-credentials");
    case ErrorCode.INACTIVE_AUTOMATION:
      return t("errors.automation-is-inactive");
    case ErrorCode.CHOICE_MISSING:
      return t("errors.unable-to-find-choice", {
        input: getValueFromStructField(error.attributes?.fields.input),
      });
    case ErrorCode.LINKED_RECORD_TITLE_MISSING:
      return t("errors.found-not-enough-linked-record(s)-for-titles", {
        foundCount: getValueFromStructField(
          error.attributes?.fields.foundCount,
        ),
        valuesCount: getValueFromStructField(
          error.attributes?.fields.valuesCount,
        ),
        titles: getValueFromStructField(error.attributes?.fields.titles),
      });
    case ErrorCode.ACTION_FAILED:
      return `${t("errors.action-fail")}: ${translateIfPresent(
        "caption." + error.message,
        error.message,
      )}`;
    case ErrorCode.EXTERNAL_SERVICE_ERROR:
      return error.message;
    case ErrorCode.USAGE_LIMIT_REACHED:
      return t("errors.usage-limit-reached");
    case ErrorCode.CONNECT_TIMEOUT:
      return t("errors.connect-timeout-error");
    case ErrorCode.BAD_REQUEST:
      return t("errors.bad-request");
    case ErrorCode.APPLICATION_MISSING:
      if (
        error.attributes?.fields.message !== undefined &&
        error.attributes?.fields.message.kind.oneofKind === "stringValue"
      ) {
        return error.attributes?.fields.message.kind.stringValue;
      } else if (error.message !== "") {
        return `${error.message}`;
      }
      return t("errors.table-is-missing");
    case ErrorCode.ACTION_TIMEOUT:
      return t("errors.action-timeout-error");
    case ErrorCode.EXPIRED_CREDENTIALS:
      return t("errors.expired-credentials");
    case ErrorCode.NOT_FOUND:
      return t("errors.not-found");
    case ErrorCode.FIELD_MISSING:
      return t("errors.field-missing", {
        fieldId: getValueFromStructField(error.attributes?.fields.fieldId),
      });
    case ErrorCode.NO_RECORDS_FOUND:
      return t("errors.records-not-found");
    case ErrorCode.RECORDS_LIMIT_REACHED:
      return t("errors.records-limit-reached");
    case ErrorCode.TOO_MANY_REQUESTS:
      return t("errors.too-many-requests");
    case ErrorCode.UNSUPPORTED_FILE_TYPE:
      return t("errors.unsupported-attachment-type", {
        fileExtension: getValueFromStructField(
          error.attributes?.fields.fileExtension,
        ),
      });
    case ErrorCode.INPUT_DATA_SIZE:
      return error.message;
  }
  return "";
};

const getValueFromStructField = (item: Value | undefined): string => {
  if (item === undefined) {
    return "";
  }
  if (item.kind.oneofKind === "stringValue") {
    return item.kind.stringValue;
  }
  if (item.kind.oneofKind === "listValue") {
    return item.kind.listValue.values
      .map((value) => getValueFromStructField(value))
      .join("; ");
  }
  return "";
};

const getJobErrorMessage = (job: LimitedJob): string | undefined => {
  switch (job.status?.stage) {
    case JobStatus_Stage.ACTION_FAIL:
    case JobStatus_Stage.AUTOMATION_DISABLED:
    case JobStatus_Stage.TRIGGER_FAIL:
      return job.status.error?.message ?? job.status.message;
    case JobStatus_Stage.STARTED:
    case JobStatus_Stage.COMPLETED:
    case JobStatus_Stage.CYCLE_DETECTED:
    default:
      return undefined;
  }
};

export const findRecordValue = (
  outputId: string,
  record?: RecordOutputData,
): string => {
  if (record === undefined) {
    return "";
  }
  const output = record.outputs.find((output) => output.outputId === outputId);
  if (output?.value === undefined) {
    return "";
  }
  const value = pickValueDataString(output.value);
  return value ?? "";
};

export const findRecordIdValue = (record?: RecordJobData): string => {
  if (record === undefined) {
    return "";
  }
  const recordIdOutput =
    record.outputData?.recordList?.recordList !== undefined
      ? record.outputData.recordList.recordList.records[0]?.outputs.find(
          (output) => output.outputId === RECORD_ID,
        )
      : undefined;

  if (recordIdOutput?.value === undefined) {
    return "";
  }

  const value = pickValueDataString(recordIdOutput.value);
  return value ?? "";
};

export const findOutputValueStringWithLimit = (
  outputId: string,
  outputs: OutputData[],
): string => {
  if (outputs.length === 0) {
    return "";
  }
  const output = outputs.find((output) => output.outputId === outputId);
  if (output?.value === undefined) {
    return "";
  }
  const value = valueDataToString(output.value);
  return value.length > OUTPUT_TEXT_LIMIT
    ? value.slice(0, OUTPUT_TEXT_LIMIT) + " ..."
    : value;
};

export const jobsToRecords = (
  jobs: LimitedJob[],
  isJobCanBeRetried: (jobState: LimitedJob) => boolean,
): ActivityRecord[] => {
  return jobs.map((job) => {
    return {
      id: job.jobId,
      datetime:
        job.retryStatus?.retryAt === undefined
          ? job.triggerTime !== undefined
            ? Timestamp.toDate(job.triggerTime).toISOString()
            : ""
          : Timestamp.toDate(job.retryStatus.retryAt).toISOString(),

      status: convertStatus(job),
      duration:
        job.retryStatus?.retryAt === undefined
          ? job.triggerTime !== undefined && job.finishTime !== undefined
            ? Number(job.finishTime.seconds) - Number(job.triggerTime.seconds)
            : 0
          : job.finishTime !== undefined
            ? Number(job.finishTime.seconds) -
              Number(job.retryStatus.retryAt.seconds)
            : 0,
      actions: job.usedActions,
      errorStage: job.status?.stage,
      errorMessage: getJobErrorMessage(job) ?? "",
      isJobCanBeRetried:
        job.status === undefined ? false : isJobCanBeRetried(job),
    };
  });
};

const getDescriptionInputLabel = (
  input: InputData,
  selectedActionFallback?: ActionCandidate,
): string => {
  const inputDescription = selectedActionFallback?.description.inputs.find(
    (inputDescription) => inputDescription.inputId === input.inputId,
  );
  return inputDescription?.label ?? "";
};

export const getInputLabel = (
  input: InputData,
  jobDetails: JobDetails,
  action: ActionOutputData,
  actions: ActionCandidate[],
  selectedActionFallback?: ActionCandidate,
  index?: number,
): string => {
  const {
    job: { automation },
  } = jobDetails;
  if (automation === undefined) {
    // Get label from selectedActionFallback (from dynamic action description) for old jobs
    return getDescriptionInputLabel(input, selectedActionFallback);
  }

  if (input.inputId === "ms-teams-input-action-field") {
    return "Actioned Field";
  }

  if (input.inputId === "ms-teams-input-action-buttons") {
    return "Response Options";
  }

  if (input.inputId === "record_id") {
    return t("job-list.record-id");
  } else if (
    input.inputId === "application_id" ||
    input.inputId === "application-id"
  ) {
    return t("job-list.app-id");
  } else if (input.inputId === "solution-id") {
    return t("job-list.solution-id");
  }

  if (index !== undefined && jobDetails.recordJobData.length > index) {
    const outputLabel = jobDetails.recordJobData[index].record?.outputs.find(
      (output) => output.outputId === input.inputId,
    )?.outputLabel;
    if (outputLabel !== undefined) {
      return translateIfPresent(`caption.${outputLabel}`, outputLabel);
    }
  }

  let actionConfig: ActionConfig | undefined;

  if (automation.actionGroups !== undefined) {
    // Deep walk (vs. only root groups before): a superset that now also
    // includes actions nested in loops. Safe here because the lookup below
    // matches by actionReference.instanceId, which is unique across the
    // whole action-group tree.
    const actionsConfigsFromGroup = collectActionsDeep(
      automation.actionGroups as ActionGroupCandidate[],
    );

    actionConfig = actionsConfigsFromGroup.find(
      (act) =>
        act.actionReference?.instanceId === action.actionReference?.instanceId,
    );
  }

  if (actionConfig === undefined) {
    actionConfig = actions.find(
      (act) =>
        act.actionReference?.instanceId === action.actionReference?.instanceId,
    );
  }

  const inputConfig = actionConfig?.inputs.find(
    (inp) => inp.inputId === input.inputId,
  );
  // Dynamic inputs (e.g. the AI action model select) are saved without a label,
  // so fall back to the label from the dynamic action description.
  const label =
    inputConfig?.label !== undefined && inputConfig.label !== ""
      ? inputConfig.label
      : getDescriptionInputLabel(input, selectedActionFallback);
  if (label === "") {
    return "";
  }
  return translateIfPresent(`caption.${label}`, label);
};

export const addEmptyValueIfNeeded = (text: string): string => {
  if (text === "") {
    return "<no value>";
  }
  return text;
};
